import { useEffect, useState, useRef } from 'react'
import { VStack, Box, Divider, Center, Flex } from '@chakra-ui/react'
import { Config } from '@!/config/config'
import { isBlank } from '@!/Util'
import { SearchForm } from '@!/Store/Common/containers'
import { PointBackBanner, MembershipRegistrationBanner, CampaignHeaderBanner } from '@!/components/Banner'
import { BookInfo } from '@!/Store/BookDetail/components/BookInfo'
import { BookList } from '@!/Store/Common/components/BookList'
import { Paginate } from '@!/components/Paginate'
import { ScrollableFeatureList, ScrollableLatelyAccessList, SectionHeader } from '@!/Store/Common/components'
import { useAuthorTitlesSearch, useMagazineTitlesSearch, useFreeTitlesSearch, useRankingTitlesSearch } from '@!/Store/BookDetail/hooks'
import { ScrollableTitles } from '@!/Store/Common/components'

const BookDetail = ({ currentBook }) => {
  const { titles: authorTitles, searchTerm: authorSearchTerm } = useAuthorTitlesSearch(currentBook)
  const { titles: magazineTitles, searchTerm: magazineSearchTerm } = useMagazineTitlesSearch(currentBook)
  const { titles: freeTitles, genreLabel, url: freeUrl } = useFreeTitlesSearch(currentBook)
  const { titles: rankingTitles, url: rankingUrl } = useRankingTitlesSearch(currentBook)
  const volumesBox = useRef(null)
  const [currentVolumes, setCurrentVolumes] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [volumeOffset, setVolumeOffset] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const volumes = currentBook.volumes
  const volumesPerPage = Config.volumesPerPage
  const campaign = currentBook.campaign_info

  useEffect(() => {
    setTotalCount(volumes.length)
    setCurrentVolumes(volumes.slice(0, volumesPerPage))
  }, [volumes])

  useEffect(() => {
    const endOffset = volumeOffset + volumesPerPage
    setCurrentVolumes(volumes.slice(volumeOffset, endOffset))
  }, [volumeOffset])

  const handlePageClick = (page) => {
    const newOffset = ((page - 1) * volumesPerPage) % volumes.length
    setVolumeOffset(newOffset)
    setCurrentPage(page)
    volumesBox.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <Box>
      {campaign ? <CampaignHeaderBanner campaign={campaign} /> : <PointBackBanner />}
      <SearchForm />
      <Divider mt={2} mb={2} />
      <VStack spacing={0}>
        <BookInfo book={currentBook} />
        <Divider borderBottomWidth="12px" mt="0" />
        <Box p={4} w="100%" boxSizing="border-box" ref={volumesBox}>
          {!isBlank(currentVolumes) ? <BookList volumes={currentVolumes} adultFlag={currentBook.adult_flag} /> : null}
        </Box>
        <Box p={4} w="100%" boxSizing="border-box">
          <Center>
            <Paginate currentPage={currentPage} perPage={volumesPerPage} totalCount={totalCount} onPageChange={handlePageClick} />
          </Center>
        </Box>
        <Divider borderBottomWidth="2px" borderColor="bg.secondary" />
        <VStack w="100%" spacing={4} my={6}>
          <Box w="100%">
            <ScrollableLatelyAccessList />
          </Box>
          {/* 著者の他の作品一覧 */}
          {!isBlank(authorTitles) && (
            <Box w="100%">
              <Flex direction="column" gap={4}>
                <ScrollableTitles titles={authorTitles} sectionTitle={`${authorSearchTerm}の作品一覧`} />
              </Flex>
            </Box>
          )}
          {/* 雑誌掲載作品一覧 */}
          {!isBlank(magazineTitles) && (
            <Box w="100%">
              <Flex direction="column" gap={4}>
                <ScrollableTitles titles={magazineTitles} sectionTitle={`${magazineSearchTerm}の作品一覧`} />
              </Flex>
            </Box>
          )}
          {/* 無料作品一覧 */}
          {!isBlank(freeTitles) && (
            <Box w="100%">
              <Flex direction="column" gap={4}>
                <ScrollableTitles titles={freeTitles} sectionTitle={`${genreLabel}ジャンルの無料作品一覧`} url={freeUrl} />
              </Flex>
            </Box>
          )}
          {/* ランキング */}
          {!isBlank(rankingTitles) && (
            <Box w="100%">
              <Flex direction="column" gap={4}>
                <ScrollableTitles titles={rankingTitles} sectionTitle="関連するランキング" url={rankingUrl} />
              </Flex>
            </Box>
          )}
          <Box w="100%">
            {!isBlank(currentBook.store_features) ? (
              <Flex direction="column" gap={4}>
                <SectionHeader key="section-header" text="オススメコミック特集" />
                {currentBook.store_features.map((feature) => (
                  <ScrollableFeatureList key={feature.pathname} title={feature.title} pathname={feature.pathname} contents={feature.contents} />
                ))}
              </Flex>
            ) : null}
          </Box>
        </VStack>
        <MembershipRegistrationBanner p={4} pt={0} />
      </VStack>
    </Box>
  )
}

export default BookDetail
